export const EXIT_GOVERNOR_MODES = ['PROTECT_PROFIT', 'BALANCED', 'GROW_PROFIT']

export const EXIT_GOVERNOR_MODE_OPTIONS = [
  { value: 'PROTECT_PROFIT', text: 'Karı Koru' },
  { value: 'BALANCED', text: 'Dengeli' },
  { value: 'GROW_PROFIT', text: 'Karı Büyüt' },
]

export function resolveExitGovernorMode(value) {
  return EXIT_GOVERNOR_MODES.includes(value) ? value : 'BALANCED'
}

export function exitGovernorModeLabel(mode) {
  return EXIT_GOVERNOR_MODE_OPTIONS.find(item => item.value === resolveExitGovernorMode(mode))?.text || 'Dengeli'
}

export function exitGovernorProfile(mode) {
  if (mode === 'PROTECT_PROFIT') return 'PROTECTIVE'
  if (mode === 'GROW_PROFIT') return 'AGGRESSIVE'
  return 'BALANCED'
}

export function buildExitGovernorPreset(modeInput) {
  const mode = resolveExitGovernorMode(modeInput)
  if (mode === 'PROTECT_PROFIT') {
    return {
      exitGovernor: { mode },
      positionGuardian: {
        profile: 'PROTECTIVE',
        breakEvenMoveMfe: 0.55,
        profitMoveMfe: 0.70,
        profitMoveMinBuffer: 0.10,
        minMfeToProtect: 0.40,
        maxGivebackPercent: 35,
        minProfitCloseAvgWinRatio: 85,
        mfeProgressWatchEnabled: true,
        mfeProgressWatchStart: 0.30,
        mfeProgressWatchMinutes: 20,
        mfeProgressMinBridgePercent: 60,
        mfeProgressMaxLoss: 0.08,
      },
      positionManagement: {
        breakEven: {
          enabled: true,
          triggerProfit: 0.30,
          bufferProfit: 0.03,
          includeFees: true,
          bufferMode: 'NET',
          slippageBufferPct: 0.05,
        },
        partialTp: {
          enabled: true,
          atProfit: 0.45,
          takePercent: 60,
          moveRemainderToBreakEven: true,
        },
        trailTp: {
          enabled: true,
          triggerProfit: 0.60,
          trailPercent: 0.30,
        },
      },
      stepStop: {
        enabled: true,
        steps: [
          { atProfit: 0.30, moveStopTo: 0.05 },
          { atProfit: 0.55, moveStopTo: 0.25 },
          { atProfit: 0.85, moveStopTo: 0.55 },
          { atProfit: 1.10, moveStopTo: 0.80 },
        ],
      },
    }
  }
  if (mode === 'GROW_PROFIT') {
    return {
      exitGovernor: { mode },
      positionGuardian: {
        profile: 'AGGRESSIVE',
        breakEvenMoveMfe: 0.60,
        profitMoveMfe: 0.95,
        profitMoveMinBuffer: 0.14,
        minMfeToProtect: 0.55,
        maxGivebackPercent: 60,
        minProfitCloseAvgWinRatio: 90,
        mfeProgressWatchEnabled: true,
        mfeProgressWatchStart: 0.40,
        mfeProgressWatchMinutes: 20,
        mfeProgressMinBridgePercent: 50,
        mfeProgressMaxLoss: 0.12,
      },
      positionManagement: {
        breakEven: {
          enabled: true,
          triggerProfit: 0.45,
          bufferProfit: 0.05,
          includeFees: true,
          bufferMode: 'NET',
          slippageBufferPct: 0.05,
        },
        partialTp: {
          enabled: true,
          atProfit: 0.70,
          takePercent: 25,
          moveRemainderToBreakEven: false,
        },
        trailTp: {
          enabled: true,
          triggerProfit: 0.90,
          trailPercent: 0.45,
        },
      },
      stepStop: {
        enabled: true,
        steps: [
          { atProfit: 0.70, moveStopTo: 0.08 },
          { atProfit: 1.10, moveStopTo: 0.40 },
          { atProfit: 1.60, moveStopTo: 0.90 },
        ],
      },
    }
  }
  return {
    exitGovernor: { mode: 'BALANCED' },
    positionGuardian: {
      profile: 'BALANCED',
      breakEvenMoveMfe: 0.55,
      profitMoveMfe: 0.75,
      profitMoveMinBuffer: 0.10,
      minMfeToProtect: 0.30,
      maxGivebackPercent: 45,
      minProfitCloseAvgWinRatio: 70,
      mfeProgressWatchEnabled: true,
      mfeProgressWatchStart: 0.30,
      mfeProgressWatchMinutes: 15,
      mfeProgressMinBridgePercent: 55,
      mfeProgressMaxLoss: 0.10,
    },
    positionManagement: {
      breakEven: {
        enabled: true,
        triggerProfit: 0.35,
        bufferProfit: 0.05,
        includeFees: true,
        bufferMode: 'NET',
        slippageBufferPct: 0.05,
      },
      partialTp: {
        enabled: true,
        atProfit: 0.50,
        takePercent: 40,
        moveRemainderToBreakEven: true,
      },
      trailTp: {
        enabled: true,
        triggerProfit: 0.65,
        trailPercent: 0.35,
      },
    },
    stepStop: {
      enabled: true,
      steps: [
        { atProfit: 0.50, moveStopTo: 0.10 },
        { atProfit: 0.80, moveStopTo: 0.35 },
        { atProfit: 1.10, moveStopTo: 0.65 },
      ],
    },
  }
}

export function applyExitGovernorModePreset(config = {}, modeInput) {
  const mode = resolveExitGovernorMode(modeInput || config?.exitGovernor?.mode)
  const preset = buildExitGovernorPreset(mode)
  const positionManagement = config.positionManagement || {}
  return {
    ...config,
    exitGovernor: {
      ...(config.exitGovernor || {}),
      ...preset.exitGovernor,
    },
    positionGuardian: {
      ...(config.positionGuardian || {}),
      ...preset.positionGuardian,
    },
    positionManagement: {
      ...positionManagement,
      breakEven: {
        ...(positionManagement.breakEven || {}),
        ...preset.positionManagement.breakEven,
      },
      partialTp: {
        ...(positionManagement.partialTp || {}),
        ...preset.positionManagement.partialTp,
      },
      trailTp: {
        ...(positionManagement.trailTp || {}),
        ...preset.positionManagement.trailTp,
      },
    },
    stepStop: {
      enabled: preset.stepStop.enabled,
      steps: preset.stepStop.steps.map(step => ({ ...step })),
    },
  }
}

export function exitGovernorSummary(modeInput) {
  const mode = resolveExitGovernorMode(modeInput)
  if (mode === 'PROTECT_PROFIT') {
    return [
      'Erken koruma, daha sıkı giveback.',
      'Partial TP daha ağır basar.',
      'Runner alanından önce karı kilitlemeyi hedefler.',
    ]
  }
  if (mode === 'GROW_PROFIT') {
    return [
      'Koruma daha geç gelir.',
      'Runner alanı daha geniş bırakılır.',
      'Kazananları daha uzun taşımayı dener.',
    ]
  }
  return [
    'Koruma ve runner alanı dengeli tutulur.',
    'Partial, trail ve step stop orta ayardadır.',
    'Genel kullanım için varsayılan baseline.',
  ]
}

export function exitGovernorReentrySummary(exitGovernor) {
  const cooldown = exitGovernor && exitGovernor.reentryCooldown ? exitGovernor.reentryCooldown : null
  if (!cooldown || !cooldown.enabled) return ['Re-entry cooldown kapali.']
  const applyOn = Array.isArray(cooldown.applyOn) && cooldown.applyOn.length
    ? cooldown.applyOn.join(', ')
    : 'SOFT_EXIT_LOSS'
  return [
    `Symbol cooldown: ${Number(cooldown.symbolMinutes || 0)} dk`,
    `Family cooldown: ${Number(cooldown.familyMinutes || 0)} dk`,
    `Apply on: ${applyOn}`,
  ]
}

function normalizeManagedState(config = {}) {
  return JSON.stringify({
    mode: resolveExitGovernorMode(config?.exitGovernor?.mode),
    positionGuardian: {
      profile: config?.positionGuardian?.profile,
      breakEvenMoveMfe: Number(config?.positionGuardian?.breakEvenMoveMfe || 0),
      profitMoveMfe: Number(config?.positionGuardian?.profitMoveMfe || 0),
      profitMoveMinBuffer: Number(config?.positionGuardian?.profitMoveMinBuffer || 0),
      minMfeToProtect: Number(config?.positionGuardian?.minMfeToProtect || 0),
      maxGivebackPercent: Number(config?.positionGuardian?.maxGivebackPercent || 0),
      minProfitCloseAvgWinRatio: Number(config?.positionGuardian?.minProfitCloseAvgWinRatio || 0),
      mfeProgressWatchEnabled: config?.positionGuardian?.mfeProgressWatchEnabled !== false,
      mfeProgressWatchStart: Number(config?.positionGuardian?.mfeProgressWatchStart || 0),
      mfeProgressWatchMinutes: Number(config?.positionGuardian?.mfeProgressWatchMinutes || 0),
      mfeProgressMinBridgePercent: Number(config?.positionGuardian?.mfeProgressMinBridgePercent || 0),
      mfeProgressMaxLoss: Number(config?.positionGuardian?.mfeProgressMaxLoss || 0),
    },
    positionManagement: {
      breakEven: config?.positionManagement?.breakEven || {},
      partialTp: config?.positionManagement?.partialTp || {},
      trailTp: config?.positionManagement?.trailTp || {},
    },
    stepStop: config?.stepStop || {},
  })
}

export function isExitGovernorDrifted(config = {}) {
  const mode = resolveExitGovernorMode(config?.exitGovernor?.mode)
  const current = normalizeManagedState(config)
  const baseline = normalizeManagedState(buildExitGovernorPreset(mode))
  return current !== baseline
}

export function recommendExitGovernorModeFromPnlHealth(metrics = {}) {
  const coverage = Number(metrics.coverageRatio || 0)
  const profitFactor = Number(metrics.profitFactor || 0)
  const expectancy = Number(metrics.expectancy || 0)
  const avgWin = Number(metrics.avgWin || 0)
  const avgLossAbs = Math.abs(Number(metrics.avgLoss || 0))
  const coverageVsLoss = avgWin > 0 && avgLossAbs > 0 ? avgWin / avgLossAbs : 0

  if (coverage > 0 && coverage < 1.1) {
    return {
      recommendedMode: 'PROTECT_PROFIT',
      modeReason: `Avg win kapsaması zayif (${coverage.toFixed(2)}x). Erken break-even ve daha siki giveback korumasi daha uygun.`,
    }
  }
  if (coverageVsLoss >= 1 && profitFactor >= 1.15 && expectancy >= 0) {
    return {
      recommendedMode: 'GROW_PROFIT',
      modeReason: 'Kazananlar kayiplari tasiyor. Runner buyutmek icin daha gec partial/trail denenebilir.',
    }
  }
  return {
    recommendedMode: 'BALANCED',
    modeReason: profitFactor > 0 && profitFactor < 1.15
      ? `Profit factor ${profitFactor.toFixed(2)} bandinda. Dengeli koruma daha uygun gorunuyor.`
      : 'Net bir korumaci veya runner agirlikli ayrisma yok; dengeli baseline uygun.',
  }
}
